import { jwtDecode } from "jwt-decode";
import moment from "moment";
import { maskCNPJ } from "./masks";
import { QUERIES } from "../config/apiConfig";
import { handleDownloadPdf } from "./pdf-exporter";
import axios from "../config/axiosInstance";
import JudicialProcesses from "../components/queries/custom-queries-credit/JudicialProcesses";
import CadinQuery from "../components/queries/custom-queries-credit/CadinQuery";
import TitleStripe from "../components/common/TitleStripe";
import Protestos from "../components/common/query/Protestos";
import NoOcurrence from "../components/common/query/NoOcurrence";

export const checkIfAuthenticated = () => {
  const token = localStorage.getItem("token");

  try {
    const decodedToken = jwtDecode(token);

    const tokenExpirationDate = new Date(decodedToken.exp) * 1000;
    const currentDate = new Date();

    return tokenExpirationDate > currentDate;
  } catch (error) {
    return false;
  }
};

export const saveUserInWindow = () => {
  const token = localStorage.getItem("token");
  const decodedToken = jwtDecode(token);
  window.user = decodedToken;
};

export const calculateExpirationPeriodTest = (date) => {
  const expirationDate = moment(date);
  const today = moment();
  return expirationDate.diff(today, "days") + 1;
};

export const normalizeProbability = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;

  const normalized = value.replace(",", ".").replace("%", "").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getFirstLettersName = (fullName) => {
  const nameParts = fullName.trim().split(" ");

  const firstLetterFirstName = nameParts[0][0];
  const firstLetterLastName =
    nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : "";

  return `${firstLetterFirstName}${firstLetterLastName}`.toUpperCase();
};

// CNPJ alfanumérico (vigente para inscrições a partir de jul/2026).
// O módulo 11 é mantido, mas cada caractere passa a valer (ASCII - 48):
// dígitos 0-9 seguem 0-9 e letras valem A=17, B=18, ... Z=42.
// Os 2 dígitos verificadores continuam numéricos.
const CNPJ_DV_WEIGHTS_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_DV_WEIGHTS_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

const cnpjCharValue = (ch) => ch.charCodeAt(0) - 48;

const calcCnpjDigit = (base, weights) => {
  const soma = base
    .split("")
    .reduce((acc, ch, idx) => acc + cnpjCharValue(ch) * weights[idx], 0);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
};

export const isValidCNPJ = (cnpj) => {
  cnpj = String(cnpj ?? "")
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "");
  if (cnpj.length !== 14) return false;
  // rejeita sequências de um único caractere repetido (ex.: 00000000000000)
  if (/^([0-9A-Z])\1{13}$/.test(cnpj)) return false;
  // os dois dígitos verificadores são sempre numéricos
  if (!/^\d{2}$/.test(cnpj.slice(12))) return false;

  const d1 = calcCnpjDigit(cnpj.slice(0, 12), CNPJ_DV_WEIGHTS_1);
  const d2 = calcCnpjDigit(cnpj.slice(0, 13), CNPJ_DV_WEIGHTS_2);
  return d1 === Number(cnpj[12]) && d2 === Number(cnpj[13]);
};

export const isValidCPF = (cpf) => {
  cpf = cpf.replace(/[^\d]+/g, "");
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  let soma = 0;
  let resto;
  for (let i = 1; i <= 9; i++)
    soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.substring(9, 10))) return false;
  soma = 0;
  for (let i = 1; i <= 10; i++)
    soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) resto = 0;
  if (resto !== parseInt(cpf.substring(10, 11))) return false;
  return true;
};

export const formatToReal = (valor) => {
  if (typeof valor === "number") {
    valor = valor.toString();
  }

  if (typeof valor !== "string") return "R$ -";

  valor = valor.replace(/[^\d.,]/g, "").trim();

  let numero;

  if (valor.includes(",") && valor.includes(".")) {
    const tratado = valor.replace(/\./g, "").replace(",", ".");
    numero = parseFloat(tratado);
  } else if (valor.includes(",")) {
    numero = parseFloat(valor.replace(",", "."));
  } else {
    numero = parseFloat(valor);
  }

  if (isNaN(numero)) return "R$ -";

  return numero.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

export const base64ToBlob = (
  base64,
  mimeType = "application/octet-stream"
) => {
  if (!base64) return null;

  const cleaned = String(base64)
    .trim()
    .replace(/^data:[^;]+;base64,/, "");

  const byteArrays = [];
  const chunkSize = 1024 * 1024;
  const sliceSize = chunkSize - (chunkSize % 4);

  for (let offset = 0; offset < cleaned.length; offset += sliceSize) {
    const slice = cleaned.slice(offset, offset + sliceSize);
    const binaryString = atob(slice);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    byteArrays.push(bytes);
  }

  return new Blob(byteArrays, { type: mimeType });
};

// Gera o PDF de uma consulta JÁ SALVA no backend (endpoint /query-pdf), sem
// re-executar a consulta nem cobrar crédito, e faz o download (ou compartilha
// no mobile). Mesma abordagem usada no CustomCreditQuery.savePDFFn — evita o
// window.print() do cliente, que trava em consultas muito grandes.
export const downloadQueryPDF = async ({
  queryId,
  categoryId = 1,
  companyId,
  parameter,
  hasHeaderPDF = "true",
} = {}) => {
  if (!queryId || !parameter) return;

  const response = await axios.get(QUERIES.GENERATE_QUERY_PDF, {
    params: { queryId, categoryId: categoryId || 1, companyId, parameter, hasHeaderPDF },
  });

  const pdfBase64 = response?.data?.data?.pdfBase64;
  const documentId = response?.data?.data?.document || parameter;
  if (!pdfBase64) return;

  const blob = base64ToBlob(pdfBase64, "application/pdf");
  if (!blob) return;

  const fileName = `consulta_${documentId || "documento"}.pdf`;
  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia?.("(max-width: 767px)")?.matches;

  if (isMobile && navigator?.canShare) {
    try {
      const file = new File([blob], fileName, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fileName });
        return;
      }
    } catch {}
  }

  const url = URL.createObjectURL(blob);
  if (isMobile) {
    const newTab = window.open(url, "_blank", "noopener,noreferrer");
    if (!newTab) window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return;
  }

  const link = window.document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const resetValuesForm = (values, setValue, reset) => {
  values.forEach((value) => {
    setValue(value, "");
  });
  reset();
};

export const uploadPDF = async (component, info, queryId) => {
  const file = await handleDownloadPdf(component.current);
  localStorage.setItem("pdfFile", file);
  return file;
};

export const getImageURLScore = (score) => {
  const scoreImages = [
    "/assets/score/score_1.png",
    "/assets/score/score_2.png",
    "/assets/score/score_3.png",
    "/assets/score/score_4.png",
    "/assets/score/score_5.png",
    "/assets/score/score_6.png",
    "/assets/score/score_7.png",
    "/assets/score/score_8.png",
    "/assets/score/score_9.png",
    "/assets/score/score_10.png",
    "/assets/score/score_11.png",
    "/assets/score/score_12.png",
    "/assets/score/score_13.png",
    "/assets/score/score_14.png",
    "/assets/score/score_15.png",
  ];

  const interval = 1000 / scoreImages.length;
  const index = Math.min(Math.floor(score / interval), scoreImages.length - 1);

  return scoreImages[index];
};

export const getImageURLScoreSerasa = (score) => {
  const scoreImages = [
    "/assets/score/serasa/score_1.png",
    "/assets/score/serasa/score_2.png",
    "/assets/score/serasa/score_3.png",
  ];

  const interval = 1000 / scoreImages.length;
  const index = Math.min(Math.floor(score / interval), scoreImages.length - 1);

  return scoreImages[index];
};

export const getColorScore = (score) => {
  const colors = ["#ed505b", "#FF9700", "#f2da09", "#39EA0E", "#027B01"];

  if (score < 0) score = 0;
  if (score > 1000) score = 1000;

  const index = Math.floor(score / 200);

  return colors[index];
};

export const getColorProbability = (probability) => {
  const colors = ["#027B01", "#f2da09", "#F43240"];

  if (probability < 0) probability = 0;
  if (probability > 1000) probability = 100;

  const index = Math.floor(probability / 33);

  return colors[index];
};

export const getLetterScore = (score) => {
  const letter = [
    "E",
    "D",
    "C",
    "CC",
    "CCC",
    "B",
    "BB",
    "BBB",
    "A",
    "AA",
    "AAA",
  ];

  if (score < 0) score = 0;
  if (score > 1000) score = 1000;

  const index = Math.floor((score / 1000) * (letter.length - 1));
  return letter[index];
};

export const maskCEP = (cep) => {
  cep = cep?.replace(/\D/g, "");
  cep = cep?.replace(/^(\d{5})(\d)/, "$1-$2");

  return cep;
};

export const getScoreLegends = (letter) => {
  const scoreLegendas = {
    AAA: {
      descricao:
        "Nota mais alta possível, indicando capacidade excepcional de pagamento. O risco é extremamente baixo.",
    },
    AA: {
      descricao:
        "Muito alta capacidade de pagamento, mas com leve risco maior que AAA.",
    },
    A: {
      descricao:
        "Alta capacidade de pagamento, mas com leve risco maior que AA.",
    },
    BBB: {
      descricao:
        "Alta capacidade de pagamento, mas com leve risco maior que A.",
    },
    BB: {
      descricao:
        "Entidade com capacidade de pagar, mas dependente de condições favoráveis.",
    },
    B: {
      descricao:
        "Entidade com capacidade de pagar, mas dependente de condições favoráveis.",
    },
    CCC: {
      descricao:
        "Alto risco de inadimplência; dificuldades financeiras sérias.",
    },
    CC: {
      descricao:
        "Alto risco de inadimplência; dificuldades financeiras sérias.",
    },
    C: {
      descricao:
        "Alto risco de inadimplência; dificuldades financeiras sérias.",
    },
    D: {
      descricao: "Já em situação de inadimplência.",
    },
    E: {
      descricao: "Já em situação de inadimplência.",
    },
  };
  return scoreLegendas[letter].descricao;
};

export const maskStateSubscription = (inscricao) => {
  inscricao = inscricao.replace(/\D/g, "");
  return inscricao.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3.$4-$5"
  );
};

export const getImageScoreSale = (score) => {
  let scoreLetter;

  if (score <= 125) {
    scoreLetter = "1";
  } else if (score <= 250) {
    scoreLetter = "2";
  } else if (score <= 375) {
    scoreLetter = "3";
  } else if (score <= 500) {
    scoreLetter = "4";
  } else if (score <= 625) {
    scoreLetter = "5";
  } else if (score <= 750) {
    scoreLetter = "6";
  } else if (score <= 875) {
    scoreLetter = "7";
  } else {
    scoreLetter = "8";
  }

  return `/assets/score/score_${scoreLetter}.png`;
};

export const calculateRiskScore = (
  pendencias,
  protestos,
  score,
  probabilidade
) => {
  let maxScore = 1000;
  const pesoPendencias = 25;
  const pesoProtestos = 25;

  if (score > 800) {
    maxScore = 850;
  } else if (score > 600) {
    maxScore = 650;
  } else if (score > 400) {
    maxScore = 500;
  } else {
    maxScore = 300;
  }

  if (probabilidade / 100 > 85) {
    maxScore -= 100;
  }

  if (pendencias > 0 || protestos > 0) {
    const penalidade = pendencias * pesoPendencias + protestos * pesoProtestos;

    return Math.max(0, maxScore - penalidade);
  }

  return Math.max(0, maxScore);
};

export const formatDateToYY = (input) => {
  if (typeof input !== "string") return input;

  const match = input.trim().match(/^([A-Za-z]{3})\/(\d{4})$/i);
  if (!match) return input;

  const [, month, year] = match; // year = '2025'
  const yy = year.slice(-2); // '25' (últimos 2 dígitos)

  return `${month}/${yy}`; // ex.: 'Jul/25'
};

export const formatDateToDDMM = (dateString) => {
  const month = dateString.slice(0, 2);
  const year = dateString.slice(2);

  const shortYear = year.slice(-2);

  return `${month}/${shortYear}`;
};

export const normalizeText = (text) => {
  return text
    .normalize("NFD") // Decompõe caracteres acentuados
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/ç/g, "c") // Substitui 'ç' por 'c'
    .replace(/\s+/g, "-") // Substitui espaços por '-'
    .replace(/[^a-zA-Z0-9-]/g, "") // Remove tudo que não for letra, número ou '-'
    .toLowerCase(); // Converte para minúsculas
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (error) => reject(error));
    img.setAttribute("crossOrigin", "anonymous"); // evita problemas com CORS
    img.src = url;
  });

const getRadianAngle = (degreeValue) => (degreeValue * Math.PI) / 180;

export const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = (error) => reject(error);
      img.src = url;
    });

  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const rad = (rotation * Math.PI) / 180;

  // Define o tamanho do resultado final
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Preenche o fundo com preto
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Cria canvas temporário para rotação e manipulação
  const tempCanvas = document.createElement("canvas");
  const tempCtx = tempCanvas.getContext("2d");

  const maxSize = Math.max(image.width, image.height);
  const safeArea = maxSize * 2; // garante que a imagem caiba com rotação

  tempCanvas.width = safeArea;
  tempCanvas.height = safeArea;

  // Centraliza e rotaciona a imagem no canvas temporário
  tempCtx.translate(safeArea / 2, safeArea / 2);
  tempCtx.rotate(rad);
  tempCtx.drawImage(image, -image.width / 2, -image.height / 2);

  // Agora extraímos a área cropada correta do canvas temporário
  const cropX = safeArea / 2 - image.width / 2 + pixelCrop.x;
  const cropY = safeArea / 2 - image.height / 2 + pixelCrop.y;

  ctx.drawImage(
    tempCanvas,
    cropX,
    cropY,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        const file = new File([blob], "cropped.jpeg", { type: "image/jpeg" });
        resolve(file);
      },
      "image/jpeg",
      1
    );
  });
};

const getRotatedSize = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

const hasRestriction = (valor) => {
  if (valor === undefined || valor === null) return false;

  if (Array.isArray(valor)) return valor.length > 0; // Se é array

  return String(valor) !== "0";
};

export const checkIfHasRestrictions = (data) => {
  return (
    (Array.isArray(data?.leilaoConjugado?.ocorrencias) &&
      data.leilaoConjugado.ocorrencias.some((l) =>
        hasRestriction(l?.ocorrencias)
      )) ||
    (Array.isArray(data?.leilaoBase1?.ocorrencias) &&
      data.leilaoBase1.ocorrencias.some((l) =>
        hasRestriction(l?.ocorrencias)
      )) ||
    (Array.isArray(data?.leilaoSimples?.ocorrencias) &&
      data.leilaoSimples.ocorrencias.some((l) =>
        hasRestriction(l?.ocorrencias)
      )) ||
    hasRestriction(data?.remarketing?.ocorrencias) ||
    hasRestriction(data?.recall?.ocorrencias) ||
    hasRestriction(data?.renajud?.temRenajud) ||
    hasRestriction(data?.renainf?.temRenainf) ||
    hasRestriction(data?.historicoRouboEFurto?.temHistoricoRouboEFurto) ||
    hasRestriction(data?.indicioSinistro?.temIndicio) ||
    hasRestriction(data?.indicioSinistro1?.temIndicio) ||
    hasRestriction(data?.indicioSinistro3?.temIndicio) ||
    hasRestriction(data?.indicioSinistroIntegral?.temIndicio) ||
    hasRestriction(data?.gravame?.temGravame) ||
    hasRestriction(data?.binEstadual?.restricoes?.existeRestricaoRouboFurto) ||
    hasRestriction(data?.binEstadual?.restricoes?.ipva?.existePendencia) ||
    hasRestriction(data?.binEstadual?.restricoes?.dpvat?.existePendencia) ||
    hasRestriction(data?.binEstadual?.restricoes?.multas?.existePendencia) ||
    hasRestriction(
      data?.binEstadual?.restricoes?.licenciamento?.existePendencia
    )
  );
};

export const formatDocument = (valor) => {
  if (!valor) return "";

  // CNPJ alfanumérico: presença de letra ⇒ CNPJ; senão decide por comprimento.
  const alnum = String(valor)
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, "");
  const isCnpj = /[A-Z]/.test(alnum) || alnum.length > 11;

  if (!isCnpj) {
    const numeros = alnum.replace(/\D/g, "").padStart(11, "0"); // completa CPF
    return numeros
      ?.replace(/^(\d{3})(\d)/, "$1.$2")
      ?.replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      ?.replace(/\.(\d{3})(\d)/, ".$1-$2");
  }

  return maskCNPJ(alnum.padStart(14, "0")); // completa CNPJ e aplica máscara
};

export const formatThousands = (n, { locale = "pt-BR", decimals = 0 } = {}) => {
  if (n == null || isNaN(n)) return "-";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(n));
};

export function countDadosCadastrais(obj) {
  if (!obj || typeof obj !== "object") return 0;

  let count = 0;

  if (obj.hasOwnProperty("dadosCadastrais")) {
    count += 1;
  }

  for (const key in obj) {
    if (typeof obj[key] === "object") {
      count += countDadosCadastrais(obj[key]);
    }
  }

  return count;
}

export function countScore(obj) {
  if (!obj || typeof obj !== "object") return 0;

  let count = 0;

  if (obj.hasOwnProperty("score")) {
    count += 1;
  }

  for (const key in obj) {
    if (typeof obj[key] === "object") {
      count += countScore(obj[key]);
    }
  }

  return count;
}

export const parseJSON = (value) => {
  try {
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch (e) {
    console.error("Erro ao parsear JSON:", e);
    return null;
  }
};

export function parseFoundationDate(str) {
  if (!str) return null;
  const formats = [
    "YYYY-MM-DD",
    "DD/MM/YYYY",
    "DD-MM-YYYY",
    "YYYYMMDD",
    "YYYY/MM/DD",
  ];

  let m = moment(str, formats, true);
  if (!m.isValid()) m = moment(str);
  return m.isValid() ? m : null;
}

export function getCompanyAge(mdate) {
  if (!mdate) return null;
  const now = moment();
  if (mdate.isAfter(now)) return { years: 0, months: 0 };
  const years = now.diff(mdate, "years");
  const months = now.diff(mdate.clone().add(years, "years"), "months");
  return { years, months };
}

export function isOlderThan30Days(isoString) {
  const d = moment(isoString, moment.ISO_8601, true);
  if (!d.isValid()) return false;
  return d.isSameOrBefore(moment().subtract(60, "days"));
}

// ===== Sinalização de restrições por fonte =====

const SOURCE_SETS = {
  boavista: new Set([
    "defineRisco",
    "defineNegocioPositivo",
    "defineLimite",
    "defineLimitePositivo",
    "acertaEssencialPositivo",
    "acertaEssencial",
    "acertaCompleto",
  ]),
  serasa: new Set(["crednet", "serasa", "serasaPremium"]),
  spc: new Set([
    "spcMixTopPF",
    "spcMixTopPJ",
    "spcSerasaPF",
    "spcSerasaPJ",
    "pendSPCSerasaPF",
    "protestoNacional",
    "chequesSemFundo",
    "scpcNetPF",
    "scpcNet",
  ]),
  quod: new Set(["quod"]),
};

export const hasRestrictionForKey = (key, item) => {
  if (!item) return false;

  const get = (obj, path, def = undefined) =>
    path
      .split(".")
      .reduce((o, p) => (o && o[p] != null ? o[p] : undefined), obj) ?? def;

  const gt = (v) => {
    if (typeof v === "number") return v > 0;
    if (typeof v === "string") {
      const n = Number(
        String(v)
          .replace(/[^\d,-]/g, "")
          .replace(".", "")
          .replace(",", ".")
      );
      return !isNaN(n) && n > 0;
    }
    return false;
  };

  // Estruturas comuns: consultaCredito.resumoConsulta ou resumoConsulta direto
  const cc = item?.consultaCredito?.resumoConsulta || item?.resumoConsulta;
  if (cc) {
    const buckets = [
      cc?.pendenciasFinanceiras,
      cc?.protestos,
      cc?.chequesSemFundo,
      cc?.chequesSustados,
      cc?.acoesCiveis,
      cc?.serasa,
      cc?.spc,
    ].filter(Boolean);

    for (const b of buckets) {
      if (gt(b?.quantidadeTotal) || gt(b?.valorTotal)) return true;
    }
  }

  // Ocorrências (protestoNacional, chequesSemFundo etc.)
  if (Array.isArray(item?.ocorrencias) && item.ocorrencias.length > 0)
    return true;

  // SCR Bacen
  if (key === "scrBacen") {
    const v1 = get(item, "scrBacen.consolidado.creditoVencido.valor", "0");
    const v2 = get(item, "scrBacen.consolidado.prejuizo.valor", "0");
    return gt(v1) || gt(v2);
  }

  // CADIN
  if (key === "cadin") {
    const occ = item?.ocorrencias || [];
    const count = occ.reduce(
      (t, o) =>
        t +
        (Array.isArray(o?.dividas)
          ? o.dividas.reduce((s, d) => s + (Number(d?.quantidade) || 0), 0)
          : 0),
      0
    );
    return count > 0;
  }

  // DefineLimite / DefineLimitePositivo (ausência de 'mensagem' + totais > 0)
  if (key === "defineLimite" || key === "defineLimitePositivo") {
    const blocks = [
      "cheques_sem_fundo",
      "cheques_sustados",
      "protestos",
      "pendencias_restricoes",
      "acoes_civeis",
      "falencias_recuperacao_judicial",
    ];
    for (const b of blocks) {
      const sec = item?.[b];
      if (!sec) continue;
      const hasMsg = !!sec?.mensagem;
      const totals = Object.values(sec).filter((v) => typeof v === "number");
      if (!hasMsg && (totals.some((n) => n > 0) || Object.keys(sec).length > 1))
        return true;
    }
  }

  return false;
};

export const isSourceRestricted = (source, data) => {
  const set = SOURCE_SETS[source];
  if (!data || !set) return false;
  for (const key of set) {
    if (data?.[key] && hasRestrictionForKey(key, data[key])) return true;
  }
  return false;
};

/**
 * Percorre recursivamente um objeto/array em busca do código de assunto CNJ 4993
 * (Recuperação Judicial e Falência). Retorna true assim que encontrar.
 */
export const hasFalenciaRecuperacaoCode = (payload, targetCode = "4993") => {
  const target = String(targetCode);
  const visited = new WeakSet();

  const walk = (node) => {
    if (node == null || typeof node !== "object") return false;
    if (visited.has(node)) return false;
    visited.add(node);

    if (Array.isArray(node)) {
      for (const item of node) {
        if (walk(item)) return true;
      }
      return false;
    }

    if (Array.isArray(node?.assuntosCNJ)) {
      const hasTarget = node.assuntosCNJ.some(
        (a) => String(a?.codigo) === target
      );
      if (hasTarget) return true;
    }

    if (node?.codigo !== undefined && String(node.codigo) === target) {
      return true;
    }

    for (const value of Object.values(node)) {
      if (walk(value)) return true;
    }

    return false;
  };

  return walk(payload);
};

export const extractFalenciaRecuperacaoJudicialList = (payload) => {
  const nested =
    payload?.falencias_recuperacao_judicial?.ultimas_ocorrencias
      ?.falencia_recuperacao_judicial;

  if (Array.isArray(nested)) return nested;
  if (nested) return [nested];

  const fallback = payload?.falenciaRecuperacaoJudicial;

  if (Array.isArray(fallback)) return fallback;
  if (fallback) return [fallback];

  return [];
};

export const renderSpecialTab = (key, data) => {
  if (key === "acoesProcessos" && data?.acoesProcessos?.statusRetorno == 1) {
    return <JudicialProcesses data={data.acoesProcessos} />;
  }

  if (key === "cadin" && data?.cadin?.statusRetorno == 1) {
    return <CadinQuery data={data.cadin} />;
  }

  if (
    key === "protestoNacional" &&
    data?.protestoNacional?.statusRetorno == 1
  ) {
    const node = data.protestoNacional;
    return (
      <div>
        <TitleStripe className="mb-4">Protesto Nacional</TitleStripe>
        {node?.ocorrencias?.length > 0 ? (
          <Protestos
            quantidadeTotal={node?.resumoConsulta?.protestos?.quantidadeTotal}
            valorTotal={node?.resumoConsulta?.protestos?.valorTotal}
            protestos={node?.ocorrencias}
            temVencimento={false}
            isNacional={true}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
    );
  }

  // nada para renderizar
  return null;
};

// raw = resposta desse provider (o JSON gigantesco que você mandou)
export function buildResumoDefinePositivoPJ(raw = {}) {
  // Protestos
  const protestos = {
    quantidadeTotal: Number(raw?.protestos?.total_protestos || 0),
    valorTotal: Number(raw?.protestos?.valor_total || 0),
  };

  // Pendências / restrições financeiras
  const pendenciasFinanceiras = {
    quantidadeTotal: Number(raw?.pendencias_restricoes?.total_pendencias || 0),
    valorTotal: Number(raw?.pendencias_restricoes?.valor || 0),
  };

  // Cheques sem fundo (não tem valor total nesse retorno, só quantidade)
  const chequesSemFundo = {
    quantidadeTotal: Number(
      raw?.cheques_sem_fundo?.total_cheques_sem_fundos || 0
    ),
  };

  // Ações cíveis
  const acoesCiveis = {
    quantidadeTotal: Number(raw?.acoes_civeis?.total_acoes || 0),
    valorTotal: Number(raw?.acoes_civeis?.valor_total || 0),
  };

  // Falências / recuperação judicial
  const falenciasRecJudiciais = {
    quantidadeTotal: Number(
      raw?.falencias_recuperacao_judicial?.quantidade_total || 0
    ),
    ultimaOcorrencia:
      raw?.falencias_recuperacao_judicial?.periodo_final || null,
  };

  return {
    protestos,
    pendenciasFinanceiras,
    acoesCiveis,
    falenciasRecJudiciais,
    chequesSemFundo,
  };
}

export const buildDurationLabel = (fromMoment, suffix = "") => {
  if (!fromMoment || !fromMoment.isValid()) return null;
  const years = moment().diff(fromMoment, "years");
  const afterYears = fromMoment.clone().add(years, "years");
  const months = moment().diff(afterYears, "months");
  const parts = [];
  if (years) parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  if (months) parts.push(`${months} mês${months === 1 ? "" : "es"}`);
  if (!parts.length) return `menos de 1 mês${suffix ? ` ${suffix}` : ""}`;
  const joined = parts.join(" e ");
  return suffix ? `${joined} ${suffix}` : joined;
};
